/**
 * SiteMenu
 *
 * Adding a new menu:
 *     1. Add a new exported constant to app/content/menus/index.js that
 *        contains the menu options as a JSON object. (Be sure to include it in
 *        the default export at the bottom.)
 *     2. If an item in your menu needs to link to another menu instead of a
 *        page, include a `secondaryMenu` attribute with the name of the menu you want
 *        to link to and set the `link` attribute to `null`:
 *        		{
 *          		icon: null,
 *          		link: null,
 *          		secondaryMenu: 'findByName',
 *            		text: 'Link name'
 *        		}
 *      4. Add the name of your new menu to the `menu` PropTypes list below.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import autobind from 'class-autobind';
import { extend, filter, has, isEmpty, isEqual, get, groupBy, indexOf, map, merge } from 'lodash';

import styles from './styles.css';

import Icon from 'components/Icon';
import Link from 'components/Link';
import Responsive from 'components/Responsive';

import { siteMenu as Lang } from 'language';
import { getFirstLetter } from 'utils';

/**
 * Render the component
 */
export class SiteMenu extends React.Component {
	constructor(props) {
		super(props);
		autobind(this);
	}

	shouldComponentUpdate(nextProps) {
		if (
			!isEqual(this.props.menu, nextProps.menu) ||
			!isEqual(this.props.breakpoints, nextProps.breakpoints) ||
			!isEqual(this.props.secondaryMenu, nextProps.secondaryMenu) ||
			!isEqual(this.props.additionalMenuItems, nextProps.additionalMenuItems) ||
			!isEqual(this.props.secondaryMenuContents, nextProps.secondaryMenuContents) ||
			!isEqual(this.props.user, nextProps.user) ||
			!isEqual(this.props.isMobileSiteMenu, nextProps.isMobileSiteMenu) ||
			!isEqual(this.props.showMobileFontsMenu, nextProps.showMobileFontsMenu)
		) {	return true; }

		return false;
	}

	componentWillUnmount() {
		const {
			isMobileSiteMenu,
			toggleMobileFontMenuView
		} = this.props;

		if (isMobileSiteMenu) {
			toggleMobileFontMenuView(false);
		}
	}

	/**
	 * Generate display permissions for User Account SubMenu
	 * @method  accountSecondaryMenuDisplayPermissions
	 * @param  {Object} user subscriptions object containing cloud and app subscription data, along with fonts purchased and order history
	 * @return  {Object}  return an object with display permissions for all user account submenu links & sections
	 */
	accountSecondaryMenuDisplayPermissions(subscriptions) {
		const {
			cloud,
			app,
			fonts,
			orderHistory,
			usageSnapshots,
			cloudExpired,
			cloudAutoRenew
		} = subscriptions;

		// based on 'user.subscriptions' store, sections and/or links in the 'account' secondary menu will show/hide
		return {
			Subscriptions: {
				show: (!!cloud || !!app || (!!cloudExpired && cloudAutoRenew)),
				'cloud-typography-dashboard': !!cloud,
				'manage-subscriptions': (!!cloud || !!app),
				'manage-expired-cloud-subscription': !app && !cloud && !!cloudExpired && cloudAutoRenew,
				'usage-reports': (!!cloud && !!usageSnapshots)
			},
			Purchases: {
				show: (!!fonts || !!orderHistory),
				'font-library': !!fonts,
				'order-history': !!orderHistory
			},
			Resources: {
				show: true,
				favorites: true
			}
		};
	}

	removeMenuQueryParams() {
		const { changeRoute, location } = this.props;
		const query = merge({}, location.query);
		delete query.menu;
		delete query.subMenu;
		changeRoute(extend({}, location, { query }));
	}

	closeMenus() {
		const {
			onSetMenu,
			onSetSubMenu,
			isMobileSiteMenu,
			toggleMobileFontMenuView
		} = this.props;

		if (isMobileSiteMenu) {
			toggleMobileFontMenuView(false);
		}

		this.removeMenuQueryParams();
		onSetMenu('');
		onSetSubMenu('');
	}

	/**
	 * Render Additional
	 *
	 * @method  renderAdditional
	 * @return  {Node}    Node populated with the section and all child links
	 */
	renderAdditional() {
		const { additionalMenuItems, isMobileSiteMenu, showMobileFontsMenu } = this.props;
		if (isMobileSiteMenu && !showMobileFontsMenu) {
			return null;
		}

		if (!additionalMenuItems || !additionalMenuItems.length) { return false; }

		const links = additionalMenuItems.map(
			(link) => {
				const click = { to: `/fonts/styles/${link.slug}` };
				return (
					<Link
						className={ classNames(styles['link--additional'], styles['link--hasIcon']) }
						key={ link.id }
						{ ...click }
					>
						{ link.label }
					</Link>
				);
			}
		);

		return (
			<nav className={ styles['nav--additional'] }>
				<Responsive>
					<h3 className={ styles.h3 }>{ Lang.additionalSection.title }</h3>
					<menu className={ styles['menu--additional'] }>
						{ links }
					</menu>
				</Responsive>
			</nav>
		);
	}

	/**
	 * If the menu contains a CTA (really just a footer for the menu with an
	 * actionable item) we render it here.
	 *
	 * @method  renderCTA
	 * @param   {Object}  menuName  Contains the menu label and menu name
	 * @return  {Node}				Formatted content for injection
	 */
	renderCTA() {
		const { menu } = this.props;
		const cta = menu.cta || null;

		// If there is no CTA, stop the process
		if (!cta) { return false; }

		const iconName = cta.icon || null;

		return (
			<footer className={ styles.footer }>
				<Link className={ classNames(styles.footer__link, styles['link--hasIcon'], styles['link--cta']) } href={ cta.link } to={ cta.link }>
					{ iconName && <Icon tertiary className={ styles.linkIcon } iconName={ iconName } /> }
					{ cta.text }
				</Link>
			</footer>
		);
	}

	/**
	 * Generate the more complex fonts menu
	 * @method  renderFontsMenu
	 * @return  {Node}  Container containing formatted menu content.
	 */
	renderFontsMenu() {
		const {
			breakpoints,
			secondaryMenu,
			secondaryMenuContents,
			isMobileSiteMenu,
			showMobileFontsMenu
		} = this.props;

		if (isMobileSiteMenu && !showMobileFontsMenu) {
			return null;
		}

		const { nested, type } = secondaryMenuContents;
		let hangingIndexHistory = null;
		let sections = secondaryMenuContents.sections;


		if (nested) {
			// Filter out all collections without a 'section' value
			sections = filter(sections, section => !isEmpty(section.section));
		}

		// Then group the collections by their 'section' key
		sections = groupBy(sections, 'section');

		const list = map(sections, (section, key) => { // <--- create section
			let breakAfterInSecondColumn = false;
			const links = map(section, (link, i) => { // <--- create links
				// If the link has a 'visible' key and its value is 'false', skip this one
				if (has(link, 'visible') && !link.visible) { return null; }
				// Does the link get a hangingIndex? Currently only menus with
				// `type='index'` get a hangingIndex. The hangingIndex is an element that hangs
				// off to the left of the list, as in "A" for the list items that
				// start with "A".
				let hangingIndex = null;
				if (type === 'indexed') {
					const firstLetter = getFirstLetter(link.text);
					if (hangingIndexHistory !== firstLetter) {
						hangingIndex = firstLetter;
					} else { hangingIndex = null; }

					hangingIndexHistory = firstLetter;
				}

				// Create the actual link
				const linked = this.renderLink(link, type, hangingIndex);

				// BEGIN 'break-after' hack
				// This is a hack to allow the find by name menu columns to be broken
				// at an arbitrary place on browsers that don't support 'break-after' css property
				// (Firefox as of 6 May 2020)
				// Remove this and accompanying style tag when support has improved
				let marginBottom;
				let lineHeight;
				if (breakpoints.gtLargeMedium && link.breakAfter) {
					// These numbers are pulled from line-height configs in our CSS
					// (see .menu-indexed)
					// If those are update, the below numbers will also need to be updated
					if (i < ((section.length / 2) - 1)) { // break is in first column
						lineHeight = breakpoints.gtXLarge ? 29 : 27; // eslint-disable-line no-magic-numbers
						marginBottom = (section.length - (2 * (i + 1))) * lineHeight;
						marginBottom = `${marginBottom}px`;
					} else if (i > ((section.length / 2) - 1)) { // break is in second column
						breakAfterInSecondColumn = i;
					}
				}

				if (breakpoints.gtLargeMedium && breakAfterInSecondColumn && i === (section.length - 1)) {
					// These numbers are pulled from line-height configs in our CSS
					// (see .menu-indexed)
					// If those are update, the below numbers will also need to be updated
					lineHeight = breakpoints.gtXLarge ? 29 : 27; // eslint-disable-line no-magic-numbers
					marginBottom = ((breakAfterInSecondColumn + 1) - (section.length - (breakAfterInSecondColumn + 1))) * lineHeight;
					marginBottom = `${marginBottom}px`;
				}
				// END 'break-after' hack

				return (
					<li
						className={ classNames(styles.linkRow, link.breakAfter && styles.breakAfter) }
						key={ link.text }
						style={ { marginBottom } }
					>
						{ linked }
					</li>
				);
			});

			return (
				<menu className={ classNames(styles['menu--group'], styles[`menu--${secondaryMenu}`]) } key={ key }>
					{ key && key !== 'null' && <h3 className={ styles.menuGroupHeader }>{ key || 'No Category' }</h3> }
					<ul className={ styles.secondaryMenuLinks }>
						{ links }
					</ul>
				</menu>
			);
		});

		return (
			<div className={ classNames(styles['menu--secondaryMenu'], styles[`menu--${secondaryMenuContents.type}`], styles[`menu--secondaryMenu-${secondaryMenu}`]) }>
				{ secondaryMenu === 'typeCapsules' &&
					<div className={ styles['menu--introHeader'] }>
						<strong>Type Capsules</strong>&nbsp;<br />
						<em>by</em> H&amp;Co
					</div>
				}

				{ secondaryMenu === 'typeCapsules' &&
					<div className={ styles['menu--introText'] }>Type Capsules are curated sets of font styles, chosen to work well together across a range of applications. Priced at $99, each type capsule includes $125 in credits toward the purchase of larger font families.</div> }
				{ list }
			</div>
		);
	}

	/**
	 * Output formatted lin for all menu types.
	 * @method  renderLink
	 * @param   {Object}  link  Object containing link data pulled from config file
	 * @param   {String}  type  The type of link
	 * @param   {String}  hangingIndex  The hanging index label, if needed
	 * @param   {Boolean} sectionDivider  Should we add space above this link?
	 * @return  {Node}  Formatted link with click events (if necessary)
	 */
	renderLink(link, type, hangingIndex) {
		const { breakpoints, secondaryMenu } = this.props;

		// Check for the presence of a `opensSubMenu` attribute. If it exists, this item points to
		// another menu instead of a page. The `link` attribute should then be ignored.
		let click = { onClick: () => this.closeMenus(), to: link.link };
		if (link.secondaryMenu) {
			if (link.opensSubMenu) {
				click = {
					onClick: () => {
						if (this.props.isMobileSiteMenu) {
							this.props.toggleMobileFontMenuView(true);
						}
						this.props.onSetSubMenu({ menu: link.secondaryMenu, counted: true });
					}
				};
			} else {
				click = { onClick: () => this.props.onSetMenu({ menu: link.secondaryMenu, counted: true }) };
			}
		}

		// If we’re in an iconic menu, set the icon name
		let iconName = null;
		if (link.icon || type === 'iconic') { iconName = link.icon; }

		// Should the link be active? Check active secondaryMenu, as well as the link against
		// the current location pathname
		const isActive =
			this.props.isMobileSiteMenu
				? false
				: secondaryMenu === link.secondaryMenu;

		const className = classNames(
			styles[`link--${type}`],
			{
				[styles['link--active']]: isActive,
				[styles['link--hasIcon']]: iconName,
				[styles['link--sectionDivider']]: link.withSectionDivider
			},
		);

		// If the link is marked as “external” set it up as a standard link
		if (link.external) {
			return (
				<a
					className={ className }
					href={ link.link }
					key={ link.slug }
					target="_blank"
				>
					{ iconName && <Icon tertiary className={ styles.linkIcon } iconName={ iconName } /> }
					{ hangingIndex && <span className={ styles.prefix }>{ hangingIndex }</span> }
					{ link.text }
				</a>
			);
		}

		if (type === 'capsules') {
			const imageStyle = { height: breakpoints.gtMedium ? `${parseInt(link.svgHeight, 10) * 1.5}px` : `${link.svgHeight}px` };

			return (
				<Link
					className={ className }
					key={ link.slug }
					{ ...click }
				>
					{ link.new && <span className={ styles.new }>new</span> }
					<span className={ styles.text }>{ <em>{ link.text }</em>} <span className={ styles.type }>Type</span> Capsule { link.price && <>&mdash;</> } { link.price && <span className={ styles.price }>{ link.price }</span> }</span>
					<img
						alt={ `${link.text} Type Capsule` }
						itemProp="image"
						src={ link.svg }
						style={ imageStyle }
					/>
				</Link>
			);
		}

		return (
			<Link
				className={ className }
				key={ link.slug }
				{ ...click }
			>
				{ iconName && <Icon tertiary className={ classNames(styles.linkIcon, styles[`linkIcon--${link.slug}`]) } iconName={ iconName } /> }
				{ hangingIndex && <span className={ styles.prefix }>{ hangingIndex }</span> }
				{ link.text }
			</Link>
		);
	}

	/**
	 * Creates menu element with header (optional) and unordered list of links.
	 * @method  renderMenu
	 * @param   {Object}  contents  The links we’ll use in the menu
	 * @param   {String}  type  The type of menu we’re rendering (passed on to renderLink())
	 * @return  {Node}  The menu node with all links prepared
	 */
	renderMenu(contents, type) {
		const header = contents.name;
		// Loop through all links and return the list item with the link contained.
		const links = contents.sections.map((link) => {
			const anchor = this.renderLink(link, type, false);
			return <li className={ styles.linkRow } key={ link.slug }>{ anchor }</li>;
		});

		// Lastly, return the menu with all links and the optional header.
		return (
			<menu
				className={
					classNames(styles['menu--group'])
				}
				key={ contents.slug }
			>
				{ header && <h3 className={ styles.menuGroupHeader }>{ header }</h3> }
				<ul className={ styles.links }>
					{ links }
				</ul>
			</menu>
		);
	}

	/**
	 * Render Sections
	 *
	 * Parse and format each section for the requested menu. This considers the
	 * different "types" of menus, such as "index" or "standard", and attaches the
	 * necessary CSS classes to ensure the appropriate layout is chosen.
	 *
	 * @method  renderPrimaryMenu
	 * @param   {Object}  menuName  Array of sections pulled from themenu
	 *                              config file using the `menu` prop
	 * @return  {Node}    Node populated with the section and all child links
	 */
	renderPrimaryMenu() {
		const {
			isMobileSiteMenu,
			menu,
			secondaryMenu,
			secondaryMenuContents,
			showMobileFontsMenu
		} = this.props;

		if (isMobileSiteMenu && showMobileFontsMenu) {
			return null;
		}

		const { type } = menu;

		let containerContent = [];
		let endSectionNow = false;
		let sectionHeaderCount = 0;
		let sectionName = null;
		let header = null;
		const blacklistSections = [];
		let hangingIndexHistory = null;

		// Loop through collection and create links using menu
		if (isEmpty(menu) || isEmpty(menu.sections)) { return false; }

		if (!menu.sections[0].slug) { return null; }

		const links = menu.sections.map((link, index) => {
			const nextIndex = index + 1;
			const nextUp = menu.sections[nextIndex] || [];
			if (
				nextUp.section && nextUp.section !== link.section
				&& indexOf(blacklistSections, nextUp.section) < 0
				&& menu.dontBreakOnHeader !== sectionHeaderCount
			) {
				endSectionNow = true;
			} else {
				endSectionNow = false;
			}

			// Include a section header only when it's different than the
			// previous AND isn't included in the `blacklistSections` array.
			if (link.section && link.section !== sectionName && indexOf(blacklistSections, link.section) < 0) {
				header = (
					<h2
						className={ classNames(
							styles.menuGroupHeader,
							sectionHeaderCount < 1 && styles.hideOnMobile
						) }
					>
						{ link.section }
					</h2>
				);
				sectionName = link.section;
				sectionHeaderCount += 1;
			} else { header = null; }

			// Does the link get a hangingIndex? Currently only menus with
			// `type='index'` get a hangingIndex. The hangingIndex is an element that hangs
			// off to the left of the list, as in "A" for the list items that
			// start with "A".
			let hangingIndex = null;
			if (type === 'indexed') {
				const firstLetter = getFirstLetter(link.text);
				if (hangingIndexHistory !== firstLetter) {
					hangingIndex = firstLetter;
				} else { hangingIndex = null; }

				hangingIndexHistory = firstLetter;
			}

			// Create the actual link
			const linked = this.renderLink(link, type, hangingIndex, link);

			// Return the formatted list
			containerContent.push(
				<div
					className={ styles.linkRow }
					key={ `${link.slug}_${index}` }
				>
					{ header }{ linked }
				</div>
			);

			if (index !== menu.sections.length - 1 && endSectionNow === false) { return false; }
			const containerJSX = (
				<div className={ styles.sectionContainer } key={ `${link.slug}_container_${index}` }>
					{ containerContent }
				</div>
			);
			containerContent = [];

			return containerJSX;
		});

		return (
			<menu
				className={ classNames(
					styles['menu--primaryMenu'],
					styles[`menu--primaryMenu-${secondaryMenu}`],
					menu && menu.type && styles[`menu--${menu.type}`],
					menu && menu.migrating && styles['menu--migrating'],
					menu && menu.hasColumnsOnDesktop && styles['menu--hasColumnsOnDesktop'],
					secondaryMenuContents && styles[`secondary--${secondaryMenuContents.type}`]
				) }
			>
				{ links }
			</menu>
		);
	}

	/**
	 * Generate the secondary menu
	 * @method  renderSecondaryMenu
	 * @return  {Node}  Return the container with the menus placed in the correct positions.
	 */
	renderSecondaryMenu() {
		const { secondaryMenuContents, user } = this.props;
		const { type } = secondaryMenuContents;

		// The columns we’ll place the menus into
		let noColumn = [];
		const columnOne = [];
		let columnTwo = [];
		// Check if the secondary menu that'll be render is 'account' and create a permissions object
		const secondaryMenuIsAccount = (secondaryMenuContents.name === 'account' && user !== null);
		const displayPermission = secondaryMenuIsAccount ? this.accountSecondaryMenuDisplayPermissions(user.subscriptions) : null;

		// Loop through the `sections` field in the menu
		secondaryMenuContents.sections.map((section) => {
			if (isEmpty(section.sections)) { return false; }

			// … then loop through the nested sections array to output all subMenus.
			const subMenus = section.sections.map((sectionLink) => {
				let updatedSectionLink = sectionLink;

				// if iterating through secondary menu of 'account', reference displayPermission object and hide/show items as configured.
				if (secondaryMenuIsAccount) {
					// if displayPermission has a hide, return.
					if (!displayPermission[sectionLink.name].show) {
						return false;
					}

					const trimSectionLink = [];

					// if the link has a true value in displayPermission, push to updatedSectionLink to be shown in secondary menu, or else hide
					sectionLink.sections.map((listItem) => {
						if (displayPermission[sectionLink.name][listItem.slug]) {
							trimSectionLink.push(listItem);
						}
						return listItem;
					});

					updatedSectionLink = { ...sectionLink, sections: trimSectionLink };
				}

				const subMenu = this.renderMenu(updatedSectionLink, type);

				// Put the menu in the designated column
				switch (updatedSectionLink.column) {
					case 2:
						columnTwo.push(subMenu);
						break;
					case 1:
						columnOne.push(subMenu);
						break;
					default:
						noColumn.push(subMenu);
						break;
				}
				return subMenu;
			});
			return subMenus;
		});

		// Default classNames value for most secondary menu
		let secondaryMenuClassName = classNames(styles['menu--secondaryMenu']);

		// If secondary menu is 'account' and cloud subscription is empty, convert layout to noColumn and change classNames value
		if (secondaryMenuIsAccount && isEmpty(columnOne)) {
			noColumn = [...columnTwo];
			columnTwo = [];
			secondaryMenuClassName = classNames(styles['menu--secondaryMenu-noColumnOne']);
		}

		return (
			<div className={ secondaryMenuClassName }>
				{ (noColumn && isEmpty(columnOne) && isEmpty(columnTwo)) && noColumn }
				{ columnOne && isEmpty(noColumn) && !isEmpty(columnOne) &&
					<div className={ styles.columnOne }>
						{ columnOne }
					</div>
				}
				{ columnTwo && isEmpty(noColumn) && !isEmpty(columnTwo) &&
					<div className={ styles.columnTwo }>
						{ columnTwo }
					</div>
				}
			</div>
		);
	}

	render() {
		const {
			menu,
			offset,
			secondaryMenuContents,
			user
		} = this.props;

		const classes = classNames(styles.nav, { [styles[`nav--offset-${offset}`]]: offset });

		const name = get(secondaryMenuContents, 'name');
		let injectSectionClassName = styles.menus;

		if (!!name && name === 'account' && user !== null) {
			injectSectionClassName = styles.menusJustifyRight;
		}

		return (
			<nav className={ classes }>
				<Icon className={ styles.close } iconName="ex" onClick={ this.closeMenus } title="Close" />
				<div className={ classNames(styles['nav--interior'], styles[`menu--${this.props.menu.name}`]) }>
					<Responsive className={ styles.menuInterior }>
						<section className={ classNames(injectSectionClassName, secondaryMenuContents && styles.secondMenuActive) }>
							{ this.renderPrimaryMenu() }
							{ secondaryMenuContents && menu.defaultSection === 'collections' && this.renderFontsMenu() }
							{ secondaryMenuContents && menu.defaultSection !== 'collections' && this.renderSecondaryMenu() }
							{ this.renderCTA() }
						</section>
					</Responsive>
					{ this.renderAdditional() }
				</div>
			</nav>
		);
	}
}

SiteMenu.propTypes = {
	additionalMenuItems: PropTypes.array,
	breakpoints: PropTypes.object.isRequired,
	changeRoute: PropTypes.func.isRequired,
	isMenuOpen: PropTypes.bool,
	isMobileSiteMenu: PropTypes.bool.isRequired,
	location: PropTypes.object.isRequired,
	menu: PropTypes.object.isRequired,
	offset: PropTypes.oneOf(['aboveBreadcrumbBar', 'belowBreadcrumbBar']),
	onIsMenuOpen: PropTypes.func.isRequired,
	toggleMobileFontMenuView: PropTypes.func.isRequired,
	onSetMenu: PropTypes.func.isRequired,
	onSetSubMenu: PropTypes.func.isRequired,
	secondaryMenu: PropTypes.string,
	secondaryMenuContents: PropTypes.object,
	showMobileFontsMenu: PropTypes.bool,
	user: PropTypes.object
};

SiteMenu.defaultProps = {
	additionalMenuItems: null,
	isMenuOpen: false,
	offset: 'belowBreadcrumbBar',
	secondaryMenu: null,
	secondaryMenuContents: null,
	showMobileFontsMenu: false,
	user: null
};

SiteMenu.displayName = 'SiteMenu';

export default SiteMenu;
